import React, { useState, useEffect, useCallback } from 'react';
import { Activity } from '../../types/Activity';
import { LocationOption } from '../../types/Location';
import { Place } from '../../types/Quotation';
import { fetchActivitiesByCity, fetchActivitiesByCityCode, fetchActivitiesByCityId } from '../../api/Activities';
import { loadCityLocations } from '../../api/Location';
import '../../css/HotelListModal.css';

interface ActivityListModalProps {
  dayIndex: number;
  place: { label: string; value: string };
  placeOptions: Place[];
  onClose: () => void;
  onSelect: (activity: Activity) => void;
}

const ActivityListModal: React.FC<ActivityListModalProps> = ({
  dayIndex,
  place,
  placeOptions,
  onClose,
  onSelect
}) => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'price' | 'duration'>('name');
  const [selectedCity, setSelectedCity] = useState<LocationOption>({ label: place.label, value: place.value, type: 'city' });


  const loadActivities = useCallback(async () => {
    const label = (selectedCity?.label || '').trim();
    let val = (selectedCity?.value || '').trim();
    if (!val && label && label.length >= 3) {
      try {
        const opts = await loadCityLocations(label);
        const match = (opts || []).find(o => o.type === 'city');
        if (match?.value) {
          val = match.value;
          setSelectedCity({ label: match.label || label, value: match.value, type: 'city' });
        }
      } catch {}
    }
    if (!val && !label) {
      setActivities([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      let activitiesData;
      // decide which parameter to use (id/code/name)
      if (/^\d+$/.test(val)) {
        // Numeric city id
        activitiesData = await fetchActivitiesByCityId(Number(val), 50);
      } else if (val.length === 3) {
        // IATA/city code
        activitiesData = await fetchActivitiesByCityCode(val, 50);
      } else {
        // City name fallback
        const name = label || val;
        activitiesData = await fetchActivitiesByCity(name, 50);
      }
      
      let safeActivitiesData: Activity[] = [];
      if (Array.isArray(activitiesData)) {
        safeActivitiesData = activitiesData;
      } else if (activitiesData && typeof activitiesData === 'object') {
        const responseObj = activitiesData as any;
        if ('data' in responseObj && Array.isArray(responseObj.data)) {
          safeActivitiesData = responseObj.data;
        } else if ('activities' in responseObj && Array.isArray(responseObj.activities)) {
          safeActivitiesData = responseObj.activities;
        } else {
          safeActivitiesData = [];
        }
      } else {
        safeActivitiesData = [];
      }
      
      setActivities(safeActivitiesData);
    } catch (error) {
      setError('Failed to load activities. Please try again.');
      setActivities([]);
    } finally {
      setLoading(false);
    }
  }, [selectedCity?.value, selectedCity?.label]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const safeActivities = Array.isArray(activities) ? activities : [];
  
  const filteredAndSortedActivities = safeActivities
    .filter(activity => {
      try {
        const matchesSearch = activity.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (activity.description && activity.description.toLowerCase().includes(searchTerm.toLowerCase()));
        
        return matchesSearch;
      } catch (error) {
        return false;
      }
    })
    .sort((a, b) => {
      try {
        switch (sortBy) {
          case 'name':
            return a.name.localeCompare(b.name);
          case 'price':
            return (a.from_price_paise || 0) - (b.from_price_paise || 0);
          case 'duration':
            return (a.duration_minutes || 0) - (b.duration_minutes || 0);
          default:
            return 0;
        }
      } catch (error) {
        return 0;
      }
    });

  // Categories computation removed

  if (loading) {
    return (
      <div className="modal-backdrop">
        <div className="hotel-modal">
          <div className="loading">Loading activities...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="hotel-modal" onClick={(e) => e.stopPropagation()}>
        <button 
          className="close-btn" 
          onClick={onClose}
          aria-label="Close modal"
          title="Close modal"
        >
          ×
        </button>

        <div className="hotel-search-form">
          <div className="hs-field">
            <label>City</label>
            <select
              value={selectedCity?.value || selectedCity?.label || ''}
              onChange={e => {
                const val = e.target.value;
                const p = placeOptions.find((p: Place) => p.value === val || p.label === val);
                if (p) setSelectedCity({ label: p.label, value: p.value || p.label, type: 'city' });
              }}
              title="City from itinerary"
            >
              {placeOptions.map((p: Place, idx: number) => {
                const val = p.value || p.label;
                return (
                  <option key={`${val}-${idx}`} value={val}>{(p.value || p.label)} — {p.label}</option>
                );
              })}
            </select>
          </div>
          <div className="hs-field">
            <label>Search Activities</label>
            <input
              type="text"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              placeholder="Search by name or description"
            />
          </div>
          {/* Category filter removed */}
        </div>

        <div className="hotel-controls">
          <div className="hc-sort">
            <span>Sort by:</span>
            <button
              className={sortBy === 'name' ? 'active' : ''}
              onClick={() => setSortBy('name')}
            >
              Name
            </button>
            <button
              className={sortBy === 'price' ? 'active' : ''}
              onClick={() => setSortBy('price')}
            >
              Price
            </button>
            <button
              className={sortBy === 'duration' ? 'active' : ''}
              onClick={() => setSortBy('duration')}
            >
              Duration
            </button>
          </div>
          <div className="hc-filter">
            <span>Found {filteredAndSortedActivities.length} activities</span>
          </div>
          <div className="hc-filter">
            <button
              className="hs-search-btn"
              onClick={() => {
                // direct trigger to ensure fetch runs immediately
                loadActivities();
              }}
            >
              Search
            </button>
          </div>
        </div>

        <div className="hotel-list">
          {error && <p className="error">{error}</p>}
          {!loading && !error && filteredAndSortedActivities.length === 0 && (
            <p className="no-results">No activities found. Try adjusting your search criteria.</p>
          )}
          {!loading && !error && filteredAndSortedActivities.map((activity, i) => (
            <div className="hotel-card" key={activity.id || i}>
              <div className="hotel-main">
                <img
                  src={activity.picture_url || '/placeholder-activity.png'}
                  alt={activity.name}
                  className="hotel-thumbnail"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
                <div className="hotel-info">
                  <h3 className="hotel-name">{activity.name}</h3>
                  <div className="hotel-stars">
                    {activity.category && (
                      <span className="category-badge">{activity.category}</span>
                    )}
                  </div>
                  <div className="hotel-amenities">
                    {activity.duration_minutes && (
                      <span className="amenity-badge">
                        {Math.floor(activity.duration_minutes / 60)}h {activity.duration_minutes % 60}m
                      </span>
                    )}
                    {activity.variants && activity.variants.length > 0 && (
                      <span className="amenity-badge">
                        {activity.variants.filter(v => v.is_active).map(v => 
                          v.mode === 'only_ticket' ? 'Only Ticket' : v.mode
                        ).join(', ')}
                      </span>
                    )}
                    {activity.city && (
                      <span className="amenity-badge">{activity.city}</span>
                    )}
                    {activity.city_code && (
                      <span className="amenity-badge">{activity.city_code}</span>
                    )}
                  </div>
                  {activity.description && (
                    <div className="activity-description">
                      {activity.description.length > 100 
                        ? `${activity.description.substring(0, 100)}...`
                        : activity.description
                      }
                    </div>
                  )}
                </div>
                <div className="hotel-rate">
                  <span className="hotel-price">
                    {activity.from_price_paise ? `₹${Math.round(activity.from_price_paise / 100).toLocaleString('en-IN')}` : 'N/A'}
                  </span>
                </div>
              </div>
              <div className="hotel-action">
                <button className="select-btn" onClick={() => onSelect(activity)}>
                  Select
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ActivityListModal;
